/* eslint-disable no-param-reassign */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';

import api from '~/services/api';
import { useAuth } from './Auth';

interface IUnreadResponse {
  total: number;
  conversations: { conversation_id: string; unread: number }[];
}

interface IHelpNotificationsData {
  unreadChats: string[];
  unreadByChat: Record<string, number>;
  unreadCount: number;
  hasUnread: boolean;
  markAsRead(chatId: string): void;
  markConversationRead(chatId: string): Promise<void>;
  setOpenChat(chatId: string | null): void;
  clearAll(): void;
}

const HelpNotificationsContext = createContext<IHelpNotificationsData>(
  {} as IHelpNotificationsData
);

const playBeep = () => {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
    osc.onended = () => ctx.close();
  } catch (e) {
    // navegador pode bloquear áudio sem interação prévia
  }
};

const isActive = () =>
  document.visibilityState === 'visible' && document.hasFocus();

const getFaviconLink = () => {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
};

export const HelpNotificationsProvider: React.FC = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [unreadByChat, setUnreadByChat] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const unreadRef = useRef<Record<string, number>>({});
  const openChatRef = useRef<string | null>(null);
  const socketRef = useRef<Socket>();
  const joinedRef = useRef<Set<string>>(new Set());
  const originalTitle = useRef(document.title);
  const originalFavicon = useRef<string>('');

  useEffect(() => {
    unreadRef.current = unreadByChat;
  }, [unreadByChat]);

  const joinRoom = useCallback((chatId: string) => {
    joinedRef.current.add(chatId);
    socketRef.current?.emit('incident:join', chatId);
  }, []);

  const markConversationRead = useCallback(async (chatId: string) => {
    const count = unreadRef.current[chatId] || 0;
    setUnreadByChat((prev) => {
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
    setTotal((t) => Math.max(0, t - count));
    try {
      await api.patch(`ai/chat/${chatId}/read`);
    } catch (e) {
      // REST é a fonte da verdade; reconcilia no próximo fetch
    }
  }, []);

  const setOpenChat = useCallback((chatId: string | null) => {
    openChatRef.current = chatId;
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const response = await api.get<IUnreadResponse>('ai/chat/unread');
      const map: Record<string, number> = {};
      response.data.conversations.forEach((item) => {
        if (item.unread > 0) map[item.conversation_id] = item.unread;
      });
      const open = openChatRef.current;
      if (open && map[open] && isActive()) {
        markConversationRead(open);
        return;
      }
      setUnreadByChat(map);
      setTotal(response.data.total);
    } catch (e) {
      // tenta novamente na próxima reconexão
    }
  }, [markConversationRead]);

  useEffect(() => {
    if (!userId) return undefined;

    const socket = io(process.env.REACT_APP_API_URL || '');
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('admin:join');
      joinedRef.current.forEach((id) => socket.emit('incident:join', id));
      fetchUnread();
    });

    socket.on(
      'chat:unread',
      (data: { conversation_id: string; unread: number; total: number }) => {
        const id = data.conversation_id;
        const previous = unreadRef.current[id] || 0;
        if (openChatRef.current === id && data.unread > 0 && isActive()) {
          markConversationRead(id);
          return;
        }
        setUnreadByChat((prev) => {
          const next = { ...prev };
          if (data.unread > 0) next[id] = data.unread;
          else delete next[id];
          return next;
        });
        setTotal(data.total);
        if (data.unread > previous) playBeep();
      }
    );

    const onFocus = () => {
      const open = openChatRef.current;
      if (open && unreadRef.current[open]) markConversationRead(open);
    };
    window.addEventListener('focus', onFocus);

    const loadRooms = async () => {
      try {
        let page = 1;
        let pages = 1;
        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await api.get<{
            data: { chat_id?: string }[];
            pages: number;
          }>('incidents', { params: { page } });
          response.data.data.forEach((incident) => {
            if (incident.chat_id && !joinedRef.current.has(incident.chat_id)) {
              joinRoom(incident.chat_id);
            }
          });
          pages = response.data.pages;
          page += 1;
        } while (page <= pages && page <= 10);
      } catch (e) {
        // tenta novamente no próximo ciclo
      }
    };

    loadRooms();
    const interval = setInterval(loadRooms, 30000);

    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
      socket.disconnect();
      socketRef.current = undefined;
      joinedRef.current = new Set();
    };
  }, [userId, joinRoom, fetchUnread, markConversationRead]);

  // Título e favicon
  useEffect(() => {
    getFaviconLink();
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>("link[rel='icon']")
    );
    links.forEach((item) => {
      if (!item.dataset.original) item.dataset.original = item.href;
    });
    if (!originalFavicon.current) {
      originalFavicon.current =
        links.find((item) => item.sizes.value === '96x96')?.dataset.original ||
        (links[0].dataset.original as string);
    }
    const setAll = (href?: string) =>
      links.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.href = href || (item.dataset.original as string);
      });

    if (total === 0) {
      document.title = originalTitle.current;
      setAll();
      return;
    }

    document.title = `Nova mensagem (${total})`;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, 64, 64);
      ctx.beginPath();
      ctx.arc(48, 16, 14, 0, 2 * Math.PI);
      ctx.fillStyle = '#e53935';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#fff';
      ctx.stroke();
      setAll(canvas.toDataURL('image/png'));
    };
    img.src = originalFavicon.current;
  }, [total]);

  const markAsRead = useCallback((chatId: string) => {
    setUnreadByChat((prev) => {
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
    setTotal((t) => Math.max(0, t - (unreadRef.current[chatId] || 0)));
  }, []);

  const clearAll = useCallback(() => {
    setUnreadByChat({});
    setTotal(0);
  }, []);

  const value = useMemo(
    () => ({
      unreadChats: Object.keys(unreadByChat),
      unreadByChat,
      unreadCount: total,
      hasUnread: total > 0,
      markAsRead,
      markConversationRead,
      setOpenChat,
      clearAll,
    }),
    [
      unreadByChat,
      total,
      markAsRead,
      markConversationRead,
      setOpenChat,
      clearAll,
    ]
  );

  return (
    <HelpNotificationsContext.Provider value={value}>
      {children}
    </HelpNotificationsContext.Provider>
  );
};

export function useHelpNotifications(): IHelpNotificationsData {
  return useContext(HelpNotificationsContext);
}
